import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Loader2, AlertTriangle, RefreshCw, Trash2, Globe, Zap, Users, Shield,
  HeartHandshake, Swords, Milestone, Film, BookOpen, History, Code2, Check,
  CloudUpload, X, Plus, ImagePlus, ImageOff, Wand2, Sparkles, Pencil,
} from "lucide-react";
import { TopNav } from "../components/TopNav";
import { EditableText } from "../components/EditableText";
import { SectionToolbar } from "../components/SectionToolbar";
import { ConfirmModal } from "../components/ConfirmModal";
import { VersionHistoryDrawer } from "../components/VersionHistoryDrawer";
import { RawJsonEditor } from "../components/RawJsonEditor";
import { StoryboardEditor } from "../components/StoryboardEditor";
import { api, apiError } from "../lib/api";

const AUTOSAVE_DELAY_MS = 800;

// Templates for adding a new item to a list section
const ITEM_TEMPLATES = {
  characters: () => ({ name: "New Character", role: "support", age: "", personality: "", appearance: "", abilities: "", backstory: "" }),
  factions: () => ({ name: "New Faction", alignment: "neutral", goal: "", description: "" }),
  relationships: () => ({ between: ["", ""], type: "alliance", description: "" }),
  conflicts: () => ({ name: "New Conflict", type: "external", description: "", stakes: "" }),
  timeline: () => ({ arc: "New Arc", title: "", summary: "", key_events: [] }),
  themes: () => "new theme",
  episode_one_outline: () => ({ scene: 1, title: "", summary: "" }),
  opening_chapter_panels: () => ({ panel: 1, scene: "", dialogue: "" }),
};

export default function ProjectPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [story, setStory] = useState({});
  const [error, setError] = useState("");
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
  const [regeneratingKey, setRegeneratingKey] = useState(null);
  const [regenTarget, setRegenTarget] = useState(null); // section key pending confirmation
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [rawJsonOpen, setRawJsonOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmGlobalRegen, setConfirmGlobalRegen] = useState(false);

  // Per-section undo/redo stacks. Keys are section keys.
  const [stacks, setStacks] = useState({}); // {[key]: {undo:[], redo:[]}}

  const savedRef = useRef({});   // last-known-saved story_context
  const debounceRef = useRef(null);
  const pollRef = useRef(null);
  const storyRef = useRef({});
  const stacksRef = useRef({});
  useEffect(() => { storyRef.current = story; }, [story]);
  useEffect(() => { stacksRef.current = stacks; }, [stacks]);

  // -------- Data loading --------
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
      const sc = data.story_context || {};
      setStory(sc);
      savedRef.current = sc;
      if (data.status === "generating") {
        pollRef.current = setTimeout(load, 4000);
      }
    } catch (e) {
      setError(apiError(e));
    }
  }, [id]);

  useEffect(() => {
    load();
    return () => {
      clearTimeout(pollRef.current);
      clearTimeout(debounceRef.current);
    };
  }, [load]);

  // -------- Autosave --------
  const scheduleAutosave = useCallback((nextStory) => {
    clearTimeout(debounceRef.current);
    setSaveState("saving");
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.patch(`/projects/${id}`, { story_context: nextStory });
        savedRef.current = data.story_context || {};
        setProject((p) => ({ ...p, ...data }));
        setSaveState("saved");
      } catch (e) {
        setError(apiError(e));
        setSaveState("error");
      }
    }, AUTOSAVE_DELAY_MS);
  }, [id]);

  const patchMeta = useCallback(async (updates) => {
    setSaveState("saving");
    try {
      const { data } = await api.patch(`/projects/${id}`, updates);
      setProject((p) => ({ ...p, ...data }));
      setSaveState("saved");
    } catch (e) {
      setError(apiError(e));
      setSaveState("error");
    }
  }, [id]);

  // -------- Section state mutation --------
  const updateSection = useCallback((key, newValue) => {
    const prevStory = storyRef.current;
    const previous = prevStory[key];
    try {
      if (JSON.stringify(previous) === JSON.stringify(newValue)) return;
    } catch { /* noop */ }
    const curStack = stacksRef.current[key] || { undo: [], redo: [] };
    const nextStory = { ...prevStory, [key]: newValue };
    const nextStacks = {
      ...stacksRef.current,
      [key]: { undo: [...curStack.undo, previous].slice(-50), redo: [] },
    };
    setStory(nextStory);
    setStacks(nextStacks);
    scheduleAutosave(nextStory);
  }, [scheduleAutosave]);

  const undoSection = useCallback((key) => {
    const cur = stacksRef.current[key];
    if (!cur || cur.undo.length === 0) return;
    const prevValue = cur.undo[cur.undo.length - 1];
    const restUndo = cur.undo.slice(0, -1);
    const currentSectionValue = storyRef.current[key];
    const nextStory = { ...storyRef.current, [key]: prevValue };
    const nextStacks = {
      ...stacksRef.current,
      [key]: { undo: restUndo, redo: [...(cur.redo || []), currentSectionValue] },
    };
    setStory(nextStory);
    setStacks(nextStacks);
    scheduleAutosave(nextStory);
  }, [scheduleAutosave]);

  const redoSection = useCallback((key) => {
    const cur = stacksRef.current[key];
    if (!cur || cur.redo.length === 0) return;
    const nextValue = cur.redo[cur.redo.length - 1];
    const restRedo = cur.redo.slice(0, -1);
    const currentSectionValue = storyRef.current[key];
    const nextStory = { ...storyRef.current, [key]: nextValue };
    const nextStacks = {
      ...stacksRef.current,
      [key]: { undo: [...(cur.undo || []), currentSectionValue], redo: restRedo },
    };
    setStory(nextStory);
    setStacks(nextStacks);
    scheduleAutosave(nextStory);
  }, [scheduleAutosave]);

  const saveVersion = useCallback(async (label) => {
    try {
      await api.post(`/projects/${id}/versions`, { label });
      setSaveState("saved");
    } catch (e) {
      setError(apiError(e));
    }
  }, [id]);

  const regenerateSection = useCallback(async (key) => {
    setRegeneratingKey(key);
    setSaveState("saving");
    try {
      // Flush any pending autosave first so we don't lose in-flight edits
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        await api.patch(`/projects/${id}`, { story_context: storyRef.current });
      }
      const previousSection = storyRef.current[key];
      const { data } = await api.post(`/projects/${id}/sections/${key}/regenerate`);
      const newSc = data.story_context || {};
      // Push previous value onto section undo stack so user can undo the regenerate
      const curStack = stacksRef.current[key] || { undo: [], redo: [] };
      setStacks({
        ...stacksRef.current,
        [key]: { undo: [...curStack.undo, previousSection].slice(-50), redo: [] },
      });
      setStory(newSc);
      savedRef.current = newSc;
      setProject((p) => ({ ...p, ...data }));
      setSaveState("saved");
    } catch (e) {
      setError(apiError(e));
      setSaveState("error");
    } finally {
      setRegeneratingKey(null);
    }
  }, [id]);

  const regenerateProject = useCallback(async () => {
    try {
      await api.post(`/projects/${id}/regenerate`);
      setProject((p) => ({ ...p, status: "generating" }));
      setStory({});
      setStacks({});
      pollRef.current = setTimeout(load, 4000);
    } catch (e) {
      setError(apiError(e));
    }
  }, [id, load]);

  const remove = async () => {
    try {
      await api.delete(`/projects/${id}`);
      navigate("/dashboard");
    } catch (e) {
      setError(apiError(e));
    }
  };

  const applyRawJson = useCallback(async (nextStory) => {
    setSaveState("saving");
    try {
      // Full replace + snapshot the previous state
      const { data } = await api.patch(`/projects/${id}`, {
        story_context: nextStory,
        snapshot: true,
        snapshot_label: "Before raw JSON edit",
      });
      savedRef.current = data.story_context || {};
      setStory(data.story_context || {});
      setStacks({});
      setProject((p) => ({ ...p, ...data }));
      setSaveState("saved");
      setRawJsonOpen(false);
    } catch (e) {
      setError(apiError(e));
      setSaveState("error");
    }
  }, [id]);

  // -------- Render helpers --------
  const sectionProps = useCallback((key) => {
    const cur = stacks[key] || { undo: [], redo: [] };
    return {
      sectionKey: key,
      canUndo: cur.undo.length > 0,
      canRedo: cur.redo.length > 0,
      onUndo: () => undoSection(key),
      onRedo: () => redoSection(key),
      onSaveVersion: () => saveVersion(`${key} snapshot`),
      onRegenerate: () => setRegenTarget(key),
      regenerating: regeneratingKey === key,
      disabled: project?.status !== "ready",
    };
  }, [stacks, regeneratingKey, project?.status, undoSection, redoSection, saveVersion]);

  const anyRegenerating = regeneratingKey !== null;

  // -------- Early returns --------
  if (error && !project) {
    return (
      <div className="mvp-page"><TopNav />
        <div className="mvp-center mvp-error" data-testid="project-error">{error}</div>
      </div>
    );
  }
  if (!project) {
    return (
      <div className="mvp-page"><TopNav />
        <div className="mvp-center"><Loader2 size={28} className="mvp-spin" /></div>
      </div>
    );
  }

  const sc = story || {};

  return (
    <div className="mvp-page" data-testid="project-page">
      <TopNav />
      <SaveIndicator state={saveState} error={error} />
      <main className="mvp-main">
        {/* HERO */}
        <div className="mvp-project-hero">
          <div className="mvp-project-cover">
            {project.cover_image_url ? (
              <img src={project.cover_image_url} alt={project.title} data-testid="project-cover" />
            ) : (
              <div className="mvp-card-cover-ph">
                {project.type === "anime" ? <Film size={40} /> : <BookOpen size={40} />}
              </div>
            )}
          </div>
          <div className="mvp-project-meta">
            <div className="mvp-project-tags">
              <span className={`mvp-badge ${project.type}`}>{project.type}</span>
              <EditableText
                value={project.genre}
                onChange={(v) => { setProject((p) => ({ ...p, genre: v })); patchMeta({ genre: v }); }}
                className="mvp-tag"
                testid="project-genre-edit"
              />
            </div>
            <EditableText
              as="h1"
              value={project.title}
              onChange={(v) => { setProject((p) => ({ ...p, title: v })); patchMeta({ title: v }); }}
              testid="project-title"
            />
            {sc.logline !== undefined && (
              <EditableText
                as="p"
                value={sc.logline || ""}
                onChange={(v) => updateSection("logline", v)}
                className="mvp-logline"
                testid="project-logline"
                multiline
                emptyLabel="Add a one-line logline…"
              />
            )}
            <EditableText
              as="p"
              value={project.description}
              onChange={(v) => { setProject((p) => ({ ...p, description: v })); patchMeta({ description: v }); }}
              className="mvp-sub"
              testid="project-description"
              multiline
            />
            <div className="mvp-project-actions">
              <button className="mvp-nav-ghost" onClick={() => setHistoryOpen(true)} data-testid="project-history-btn">
                <History size={15} /> Version History
              </button>
              <button className="mvp-nav-ghost" onClick={() => setRawJsonOpen(true)} data-testid="project-rawjson-btn">
                <Code2 size={15} /> Raw JSON
              </button>
              <button
                className="mvp-nav-ghost"
                onClick={() => setConfirmGlobalRegen(true)}
                disabled={project.status !== "ready" || anyRegenerating}
                data-testid="project-regenerate-btn"
              >
                <RefreshCw size={15} /> Regenerate All
              </button>
              <button className="mvp-nav-ghost danger" onClick={() => setConfirmDelete(true)} data-testid="project-delete-btn">
                <Trash2 size={15} /> Delete
              </button>
            </div>
          </div>
        </div>

        {project.status === "generating" && (
          <div className="mvp-generating" data-testid="project-generating">
            <Loader2 size={22} className="mvp-spin" />
            <div>
              <h3>Claude is building your story universe…</h3>
              <p>Characters, factions, power systems and timeline — usually 30–60 seconds.</p>
            </div>
          </div>
        )}

        {project.status === "failed" && (
          <div className="mvp-failed" data-testid="project-failed">
            <AlertTriangle size={20} />
            <span>Generation failed{project.error ? `: ${project.error}` : ""}.</span>
            <button className="btn-primary mvp-cta-sm" onClick={regenerateProject} data-testid="project-retry-btn">
              <RefreshCw size={15} /> Retry
            </button>
          </div>
        )}

        {project.status === "ready" && (
          <div className="mvp-story" data-testid="story-context">
            {/* Synopsis */}
            <Section icon={<Milestone size={18} />} title="Synopsis" testid="section-synopsis"
              toolbar={<SectionToolbar {...sectionProps("synopsis")} />}
            >
              <EditableText
                as="p"
                value={sc.synopsis || ""}
                onChange={(v) => updateSection("synopsis", v)}
                className="mvp-prose"
                multiline
                testid="synopsis-text"
                emptyLabel="Write a synopsis…"
              />
            </Section>

            {/* Themes */}
            {Array.isArray(sc.themes) && (
              <Section
                icon={<Milestone size={18} />}
                title="Themes"
                testid="section-themes"
                toolbar={<SectionToolbar {...sectionProps("themes")} />}
              >
                <ThemesEditor
                  themes={sc.themes || []}
                  onChange={(next) => updateSection("themes", next)}
                />
              </Section>
            )}

            {/* World */}
            {sc.world_setting !== undefined && (
              <Section
                icon={<Globe size={18} />}
                title={`World — ${sc.world_setting?.name || "unnamed"}`}
                testid="section-world"
                toolbar={<SectionToolbar {...sectionProps("world_setting")} />}
              >
                <WorldEditor
                  world={sc.world_setting || {}}
                  onChange={(next) => updateSection("world_setting", next)}
                />
              </Section>
            )}

            {/* Power system */}
            {sc.power_system !== undefined && (
              <Section
                icon={<Zap size={18} />}
                title={`Power System — ${sc.power_system?.name || "unnamed"}`}
                testid="section-power"
                toolbar={<SectionToolbar {...sectionProps("power_system")} />}
              >
                <PowerSystemEditor
                  power={sc.power_system || {}}
                  onChange={(next) => updateSection("power_system", next)}
                />
              </Section>
            )}

            {/* Characters */}
            {Array.isArray(sc.characters) && (
              <Section
                icon={<Users size={18} />}
                title="Characters"
                testid="section-characters"
                toolbar={<SectionToolbar {...sectionProps("characters")} />}
              >
                <CharactersEditor
                  characters={sc.characters}
                  onChange={(next) => updateSection("characters", next)}
                />
              </Section>
            )}

            {/* Factions */}
            {Array.isArray(sc.factions) && (
              <Section
                icon={<Shield size={18} />}
                title="Factions"
                testid="section-factions"
                toolbar={<SectionToolbar {...sectionProps("factions")} />}
              >
                <FactionsEditor
                  factions={sc.factions}
                  onChange={(next) => updateSection("factions", next)}
                />
              </Section>
            )}

            {/* Relationships */}
            {Array.isArray(sc.relationships) && (
              <Section
                icon={<HeartHandshake size={18} />}
                title="Relationships"
                testid="section-relationships"
                toolbar={<SectionToolbar {...sectionProps("relationships")} />}
              >
                <RelationshipsEditor
                  relationships={sc.relationships}
                  onChange={(next) => updateSection("relationships", next)}
                />
              </Section>
            )}

            {/* Conflicts */}
            {Array.isArray(sc.conflicts) && (
              <Section
                icon={<Swords size={18} />}
                title="Conflicts"
                testid="section-conflicts"
                toolbar={<SectionToolbar {...sectionProps("conflicts")} />}
              >
                <ConflictsEditor
                  conflicts={sc.conflicts}
                  onChange={(next) => updateSection("conflicts", next)}
                />
              </Section>
            )}

            {/* Timeline */}
            {Array.isArray(sc.timeline) && (
              <Section
                icon={<Milestone size={18} />}
                title="Story Timeline"
                testid="section-timeline"
                toolbar={<SectionToolbar {...sectionProps("timeline")} />}
              >
                <TimelineEditor
                  timeline={sc.timeline}
                  onChange={(next) => updateSection("timeline", next)}
                />
              </Section>
            )}

            {/* Episode outline (anime) */}
            {Array.isArray(sc.episode_one_outline) && (
              <Section
                icon={<Film size={18} />}
                title="Episode 1 Outline"
                testid="section-episode"
                toolbar={<SectionToolbar {...sectionProps("episode_one_outline")} />}
              >
                <ScenesEditor
                  items={sc.episode_one_outline}
                  onChange={(next) => updateSection("episode_one_outline", next)}
                  labelKey="scene"
                  labelPrefix="Scene"
                  section="episode_one_outline"
                />
              </Section>
            )}

            {/* Storyboard (available for both anime & manga) */}
            <Section
              icon={<Film size={18} />}
              title="Storyboard"
              testid="section-storyboard"
              toolbar={null}
            >
              <StoryboardEditor
                project={project}
                storyboard={sc.storyboard}
                projectId={id}
                onProjectUpdate={(data) => {
                  savedRef.current = data.story_context || {};
                  setStory(data.story_context || {});
                  setProject((p) => ({ ...p, ...data }));
                }}
                disabled={project.status !== "ready"}
              />
            </Section>

            {/* Panels (manga) */}
            {Array.isArray(sc.opening_chapter_panels) && (
              <Section
                icon={<BookOpen size={18} />}
                title="Chapter 1 Panels"
                testid="section-panels"
                toolbar={<SectionToolbar {...sectionProps("opening_chapter_panels")} />}
              >
                <PanelsEditor
                  panels={sc.opening_chapter_panels}
                  onChange={(next) => updateSection("opening_chapter_panels", next)}
                  projectId={id}
                  onProjectUpdate={(data) => {
                    savedRef.current = data.story_context || {};
                    setStory(data.story_context || {});
                    setProject((p) => ({ ...p, ...data }));
                  }}
                  disabled={project.status !== "ready"}
                />
              </Section>
            )}
          </div>
        )}
      </main>

      {/* Modals */}
      <ConfirmModal
        open={confirmDelete}
        title="Delete this project?"
        message="This will permanently remove the project and all its versions. This cannot be undone."
        confirmLabel="Delete"
        danger
        testid="delete-confirm"
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => { setConfirmDelete(false); remove(); }}
      />

      <ConfirmModal
        open={confirmGlobalRegen}
        title="Regenerate the entire story?"
        message="Claude will generate a completely new story bible from scratch. Your current story will be snapshotted to Version History so you can restore it."
        confirmLabel="Regenerate All"
        testid="regen-all-confirm"
        onCancel={() => setConfirmGlobalRegen(false)}
        onConfirm={async () => {
          setConfirmGlobalRegen(false);
          await api.post(`/projects/${id}/versions`, { label: "Before full regeneration" }).catch(() => {});
          regenerateProject();
        }}
      />

      <ConfirmModal
        open={!!regenTarget}
        title={`Regenerate "${regenTarget}"?`}
        message="Claude will replace this entire section with fresh output. A snapshot of the current version will be saved automatically so you can restore it."
        confirmLabel="Regenerate Section"
        busy={!!regeneratingKey}
        testid="regen-section-confirm"
        onCancel={() => setRegenTarget(null)}
        onConfirm={() => {
          const key = regenTarget;
          setRegenTarget(null);
          if (key) regenerateSection(key);
        }}
      />

      <VersionHistoryDrawer
        open={historyOpen}
        projectId={id}
        currentTitle={project.title}
        onClose={() => setHistoryOpen(false)}
        onRestored={(data) => {
          setProject((p) => ({ ...p, ...data }));
          setStory(data.story_context || {});
          savedRef.current = data.story_context || {};
          setStacks({});
        }}
      />

      <RawJsonEditor
        open={rawJsonOpen}
        initialValue={sc}
        busy={saveState === "saving"}
        onClose={() => setRawJsonOpen(false)}
        onSave={applyRawJson}
      />
    </div>
  );
}

// ================= Sub-editors =================

const Section = ({ icon, title, children, testid, toolbar }) => (
  <section className="mvp-section" data-testid={testid}>
    <div className="mvp-section-head">
      <h2>{icon} {title}</h2>
      {toolbar}
    </div>
    {children}
  </section>
);

const SaveIndicator = ({ state, error }) => {
  if (state === "idle") return null;
  const cfg = {
    saving:  { icon: <Loader2 size={13} className="mvp-spin" />, label: "Saving…",  cls: "mvp-save-saving" },
    saved:   { icon: <Check size={13} />,                        label: "All changes saved", cls: "mvp-save-saved" },
    error:   { icon: <AlertTriangle size={13} />,                label: error || "Save failed", cls: "mvp-save-error" },
  }[state];
  if (!cfg) return null;
  return (
    <div className={`mvp-save-indicator ${cfg.cls}`} data-testid={`save-indicator-${state}`}>
      <CloudUpload size={13} /> {cfg.icon} {cfg.label}
    </div>
  );
};

const ThemesEditor = ({ themes, onChange }) => {
  const addTheme = () => onChange([...themes, "new theme"]);
  const removeTheme = (i) => onChange(themes.filter((_, idx) => idx !== i));
  const editTheme = (i, v) => onChange(themes.map((t, idx) => idx === i ? v : t));
  return (
    <div className="mvp-chips" data-testid="themes-editor">
      {themes.map((t, i) => (
        <span key={i} className="mvp-chip mvp-chip-editable" data-testid={`theme-chip-${i}`}>
          <EditableText value={t} onChange={(v) => editTheme(i, v)} testid={`theme-${i}`} />
          <button className="mvp-chip-x" onClick={() => removeTheme(i)} aria-label="Remove" data-testid={`theme-remove-${i}`}>
            <X size={11} />
          </button>
        </span>
      ))}
      <button className="mvp-chip mvp-chip-add" onClick={addTheme} data-testid="theme-add">
        <Plus size={11} /> Add theme
      </button>
    </div>
  );
};

const WorldEditor = ({ world, onChange }) => {
  const set = (patch) => onChange({ ...world, ...patch });
  const editLoc = (i, patch) => set({ locations: (world.locations || []).map((l, idx) => idx === i ? { ...l, ...patch } : l) });
  const removeLoc = (i) => set({ locations: (world.locations || []).filter((_, idx) => idx !== i) });
  const addLoc = () => set({ locations: [...(world.locations || []), { name: "New Location", description: "" }] });
  return (
    <>
      <p className="mvp-prose">
        <EditableText value={world.name || ""} onChange={(v) => set({ name: v })} testid="world-name" emptyLabel="World name…" />
        {" · "}
        <em><EditableText value={world.era || ""} onChange={(v) => set({ era: v })} testid="world-era" emptyLabel="Era…" /></em>
      </p>
      <EditableText
        as="p"
        value={world.description || ""}
        onChange={(v) => set({ description: v })}
        className="mvp-prose"
        multiline
        testid="world-description"
        emptyLabel="Describe the world…"
      />
      <div className="mvp-mini-grid">
        {(world.locations || []).map((l, i) => (
          <div key={i} className="mvp-mini-card mvp-editable-card" data-testid={`location-${i}`}>
            <RemoveItemBtn onClick={() => removeLoc(i)} testid={`location-remove-${i}`} />
            <h4><EditableText value={l.name || ""} onChange={(v) => editLoc(i, { name: v })} testid={`location-name-${i}`} /></h4>
            <EditableText
              as="p"
              value={l.description || ""}
              onChange={(v) => editLoc(i, { description: v })}
              multiline
              testid={`location-desc-${i}`}
              emptyLabel="Describe this location…"
            />
          </div>
        ))}
        <button className="mvp-add-card" onClick={addLoc} data-testid="location-add">
          <Plus size={14} /> Add location
        </button>
      </div>
    </>
  );
};

const PowerSystemEditor = ({ power, onChange }) => {
  const set = (patch) => onChange({ ...power, ...patch });
  const editRule = (i, v) => set({ rules: (power.rules || []).map((r, idx) => idx === i ? v : r) });
  const removeRule = (i) => set({ rules: (power.rules || []).filter((_, idx) => idx !== i) });
  const addRule = () => set({ rules: [...(power.rules || []), "New rule"] });
  return (
    <>
      <p className="mvp-prose">
        <strong><EditableText value={power.name || ""} onChange={(v) => set({ name: v })} testid="power-name" emptyLabel="System name…" /></strong>
      </p>
      <EditableText
        as="p"
        value={power.description || ""}
        onChange={(v) => set({ description: v })}
        className="mvp-prose"
        multiline
        testid="power-description"
        emptyLabel="How does the power work?"
      />
      <ul className="mvp-rules" data-testid="power-rules">
        {(power.rules || []).map((r, i) => (
          <li key={i} data-testid={`power-rule-${i}`}>
            <EditableText value={r} onChange={(v) => editRule(i, v)} multiline />
            <button className="mvp-inline-x" onClick={() => removeRule(i)} aria-label="Remove rule" data-testid={`power-rule-remove-${i}`}>
              <X size={12} />
            </button>
          </li>
        ))}
      </ul>
      <button className="mvp-nav-ghost mvp-small-add" onClick={addRule} data-testid="power-rule-add">
        <Plus size={13} /> Add rule
      </button>
    </>
  );
};

const CharactersEditor = ({ characters, onChange }) => {
  const edit = (i, patch) => onChange(characters.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  const remove = (i) => onChange(characters.filter((_, idx) => idx !== i));
  const add = () => onChange([...characters, ITEM_TEMPLATES.characters()]);
  return (
    <>
      <div className="mvp-mini-grid">
        {characters.map((c, i) => (
          <div key={i} className="mvp-mini-card mvp-char mvp-editable-card" data-testid={`character-${i}`}>
            <RemoveItemBtn onClick={() => remove(i)} testid={`character-remove-${i}`} />
            <div className="mvp-char-head">
              <h4><EditableText value={c.name || ""} onChange={(v) => edit(i, { name: v })} testid={`char-name-${i}`} emptyLabel="Name…" /></h4>
              <span className={`mvp-role ${c.role}`}>
                <EditableText value={c.role || ""} onChange={(v) => edit(i, { role: v })} testid={`char-role-${i}`} emptyLabel="role" />
              </span>
            </div>
            {c.age !== undefined && (
              <p className="mvp-dim">Age: <EditableText value={c.age || ""} onChange={(v) => edit(i, { age: v })} testid={`char-age-${i}`} emptyLabel="—" /></p>
            )}
            <p><strong>Personality:</strong> <EditableText multiline value={c.personality || ""} onChange={(v) => edit(i, { personality: v })} testid={`char-personality-${i}`} emptyLabel="…" /></p>
            <p><strong>Appearance:</strong> <EditableText multiline value={c.appearance || ""} onChange={(v) => edit(i, { appearance: v })} testid={`char-appearance-${i}`} emptyLabel="…" /></p>
            <p><strong>Abilities:</strong> <EditableText multiline value={c.abilities || ""} onChange={(v) => edit(i, { abilities: v })} testid={`char-abilities-${i}`} emptyLabel="…" /></p>
            <p className="mvp-dim"><EditableText multiline value={c.backstory || ""} onChange={(v) => edit(i, { backstory: v })} testid={`char-backstory-${i}`} emptyLabel="Backstory…" /></p>
          </div>
        ))}
        <button className="mvp-add-card" onClick={add} data-testid="character-add">
          <Plus size={14} /> Add character
        </button>
      </div>
    </>
  );
};

const FactionsEditor = ({ factions, onChange }) => {
  const edit = (i, patch) => onChange(factions.map((f, idx) => idx === i ? { ...f, ...patch } : f));
  const remove = (i) => onChange(factions.filter((_, idx) => idx !== i));
  const add = () => onChange([...factions, ITEM_TEMPLATES.factions()]);
  return (
    <div className="mvp-mini-grid">
      {factions.map((f, i) => (
        <div key={i} className="mvp-mini-card mvp-editable-card" data-testid={`faction-${i}`}>
          <RemoveItemBtn onClick={() => remove(i)} testid={`faction-remove-${i}`} />
          <div className="mvp-char-head">
            <h4><EditableText value={f.name || ""} onChange={(v) => edit(i, { name: v })} testid={`faction-name-${i}`} emptyLabel="Faction name…" /></h4>
            <span className={`mvp-role ${f.alignment}`}>
              <EditableText value={f.alignment || ""} onChange={(v) => edit(i, { alignment: v })} testid={`faction-align-${i}`} emptyLabel="alignment" />
            </span>
          </div>
          <p><strong>Goal:</strong> <EditableText multiline value={f.goal || ""} onChange={(v) => edit(i, { goal: v })} testid={`faction-goal-${i}`} emptyLabel="…" /></p>
          <EditableText as="p" multiline value={f.description || ""} onChange={(v) => edit(i, { description: v })} testid={`faction-desc-${i}`} emptyLabel="Describe the faction…" />
        </div>
      ))}
      <button className="mvp-add-card" onClick={add} data-testid="faction-add">
        <Plus size={14} /> Add faction
      </button>
    </div>
  );
};

const RelationshipsEditor = ({ relationships, onChange }) => {
  const edit = (i, patch) => onChange(relationships.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const remove = (i) => onChange(relationships.filter((_, idx) => idx !== i));
  const add = () => onChange([...relationships, ITEMS_TEMPLATE_REL()]);
  const editName = (i, ni, v) => {
    const between = [...(relationships[i].between || ["", ""])];
    between[ni] = v;
    edit(i, { between });
  };
  return (
    <div>
      {relationships.map((r, i) => (
        <div key={i} className="mvp-rel mvp-editable-row" data-testid={`relationship-${i}`}>
          <RemoveItemBtn onClick={() => remove(i)} testid={`relationship-remove-${i}`} inline />
          <span className="mvp-rel-pair">
            <EditableText value={r.between?.[0] || ""} onChange={(v) => editName(i, 0, v)} testid={`rel-a-${i}`} emptyLabel="Person A" />
            {" ⟷ "}
            <EditableText value={r.between?.[1] || ""} onChange={(v) => editName(i, 1, v)} testid={`rel-b-${i}`} emptyLabel="Person B" />
          </span>
          <span className="mvp-chip">
            <EditableText value={r.type || ""} onChange={(v) => edit(i, { type: v })} testid={`rel-type-${i}`} emptyLabel="type" />
          </span>
          <EditableText as="p" multiline value={r.description || ""} onChange={(v) => edit(i, { description: v })} testid={`rel-desc-${i}`} emptyLabel="Describe the relationship…" />
        </div>
      ))}
      <button className="mvp-nav-ghost mvp-small-add" onClick={add} data-testid="relationship-add">
        <Plus size={13} /> Add relationship
      </button>
    </div>
  );
};
const ITEMS_TEMPLATE_REL = () => ({ between: ["", ""], type: "alliance", description: "" });

const ConflictsEditor = ({ conflicts, onChange }) => {
  const edit = (i, patch) => onChange(conflicts.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  const remove = (i) => onChange(conflicts.filter((_, idx) => idx !== i));
  const add = () => onChange([...conflicts, ITEM_TEMPLATES.conflicts()]);
  return (
    <div className="mvp-mini-grid">
      {conflicts.map((c, i) => (
        <div key={i} className="mvp-mini-card mvp-editable-card" data-testid={`conflict-${i}`}>
          <RemoveItemBtn onClick={() => remove(i)} testid={`conflict-remove-${i}`} />
          <div className="mvp-char-head">
            <h4><EditableText value={c.name || ""} onChange={(v) => edit(i, { name: v })} testid={`conflict-name-${i}`} emptyLabel="Conflict name…" /></h4>
            <span className="mvp-chip">
              <EditableText value={c.type || ""} onChange={(v) => edit(i, { type: v })} testid={`conflict-type-${i}`} emptyLabel="type" />
            </span>
          </div>
          <EditableText as="p" multiline value={c.description || ""} onChange={(v) => edit(i, { description: v })} testid={`conflict-desc-${i}`} emptyLabel="Describe the conflict…" />
          <p className="mvp-dim"><strong>Stakes:</strong> <EditableText multiline value={c.stakes || ""} onChange={(v) => edit(i, { stakes: v })} testid={`conflict-stakes-${i}`} emptyLabel="…" /></p>
        </div>
      ))}
      <button className="mvp-add-card" onClick={add} data-testid="conflict-add">
        <Plus size={14} /> Add conflict
      </button>
    </div>
  );
};

const TimelineEditor = ({ timeline, onChange }) => {
  const edit = (i, patch) => onChange(timeline.map((a, idx) => idx === i ? { ...a, ...patch } : a));
  const remove = (i) => onChange(timeline.filter((_, idx) => idx !== i));
  const add = () => onChange([...timeline, ITEM_TEMPLATES.timeline()]);
  const editEvent = (i, ei, v) => {
    const events = [...(timeline[i].key_events || [])];
    events[ei] = v;
    edit(i, { key_events: events });
  };
  const removeEvent = (i, ei) => {
    const events = (timeline[i].key_events || []).filter((_, idx) => idx !== ei);
    edit(i, { key_events: events });
  };
  const addEvent = (i) => {
    const events = [...(timeline[i].key_events || []), "New event"];
    edit(i, { key_events: events });
  };
  return (
    <div className="mvp-timeline">
      {timeline.map((a, i) => (
        <div key={i} className="mvp-arc mvp-editable-row" data-testid={`arc-${i}`}>
          <RemoveItemBtn onClick={() => remove(i)} testid={`arc-remove-${i}`} inline />
          <span className="mvp-arc-num">
            <EditableText value={a.arc || ""} onChange={(v) => edit(i, { arc: v })} testid={`arc-num-${i}`} emptyLabel={`Arc ${i + 1}`} />
          </span>
          <h4><EditableText value={a.title || ""} onChange={(v) => edit(i, { title: v })} testid={`arc-title-${i}`} emptyLabel="Arc title…" /></h4>
          <EditableText as="p" multiline value={a.summary || ""} onChange={(v) => edit(i, { summary: v })} testid={`arc-summary-${i}`} emptyLabel="Arc summary…" />
          <ul className="mvp-rules">
            {(a.key_events || []).map((e, ei) => (
              <li key={ei} data-testid={`arc-${i}-event-${ei}`}>
                <EditableText value={e} onChange={(v) => editEvent(i, ei, v)} multiline />
                <button className="mvp-inline-x" onClick={() => removeEvent(i, ei)} aria-label="Remove event">
                  <X size={12} />
                </button>
              </li>
            ))}
          </ul>
          <button className="mvp-nav-ghost mvp-small-add" onClick={() => addEvent(i)} data-testid={`arc-${i}-event-add`}>
            <Plus size={13} /> Add event
          </button>
        </div>
      ))}
      <button className="mvp-nav-ghost mvp-small-add" onClick={add} data-testid="arc-add">
        <Plus size={13} /> Add arc
      </button>
    </div>
  );
};

const ScenesEditor = ({ items, onChange, labelPrefix, section }) => {
  const edit = (i, patch) => onChange(items.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, ITEM_TEMPLATES[section]()]);
  return (
    <div className="mvp-timeline">
      {items.map((s, i) => (
        <div key={i} className="mvp-arc mvp-editable-row" data-testid={`scene-${i}`}>
          <RemoveItemBtn onClick={() => remove(i)} testid={`scene-remove-${i}`} inline />
          <span className="mvp-arc-num">{labelPrefix} {s.scene || i + 1}</span>
          <h4><EditableText value={s.title || ""} onChange={(v) => edit(i, { title: v })} testid={`scene-title-${i}`} emptyLabel="Scene title…" /></h4>
          <EditableText as="p" multiline value={s.summary || ""} onChange={(v) => edit(i, { summary: v })} testid={`scene-summary-${i}`} emptyLabel="Scene summary…" />
        </div>
      ))}
      <button className="mvp-nav-ghost mvp-small-add" onClick={add} data-testid="scene-add">
        <Plus size={13} /> Add scene
      </button>
    </div>
  );
};

const PanelsEditor = ({ panels, onChange, projectId, onProjectUpdate, disabled }) => {
  const edit = (i, patch) => onChange(panels.map((p, idx) => idx === i ? { ...p, ...patch } : p));
  const remove = (i) => onChange(panels.filter((_, idx) => idx !== i));
  const add = () => onChange([...panels, ITEM_TEMPLATES.opening_chapter_panels()]);

  const [busy, setBusy] = useState({}); // {[i]: bool}
  const [batchBusy, setBatchBusy] = useState(false);
  const [promptEditIndex, setPromptEditIndex] = useState(null);
  const [promptDraft, setPromptDraft] = useState("");
  const [imgError, setImgError] = useState("");

  const setPanelBusy = (i, v) => setBusy((b) => ({ ...b, [i]: v }));

  const generate = async (i, { customPrompt, upscale } = {}) => {
    if (busy[i] || disabled) return;
    setImgError("");
    setPanelBusy(i, true);
    try {
      const { data } = await api.post(
        `/projects/${projectId}/panels/${i}/image`,
        { prompt: customPrompt ?? null, upscale: !!upscale },
      );
      onProjectUpdate(data);
    } catch (e) {
      setImgError(apiError(e));
    } finally {
      setPanelBusy(i, false);
    }
  };

  const generateAll = async () => {
    if (batchBusy || disabled) return;
    setImgError("");
    setBatchBusy(true);
    try {
      await api.post(`/projects/${projectId}/panels/generate-all`);
      // Poll for updates every 5s until batch is done or all panels resolved
      let attempts = 0;
      const poll = async () => {
        try {
          const { data } = await api.get(`/projects/${projectId}`);
          onProjectUpdate(data);
          const batch = data.story_context?.panels_batch_status;
          if (batch === "done" || attempts > 60) {
            setBatchBusy(false);
            return;
          }
          attempts += 1;
          setTimeout(poll, 5000);
        } catch {
          setBatchBusy(false);
        }
      };
      setTimeout(poll, 5000);
    } catch (e) {
      setImgError(apiError(e));
      setBatchBusy(false);
    }
  };

  const openPromptEditor = (i) => {
    setPromptEditIndex(i);
    setPromptDraft(panels[i]?.image_prompt || panels[i]?.scene || "");
  };
  const savePromptEdit = async (regen) => {
    const i = promptEditIndex;
    setPromptEditIndex(null);
    try {
      const { data } = await api.patch(
        `/projects/${projectId}/panels/${i}`,
        { image_prompt: promptDraft },
      );
      onProjectUpdate(data);
      if (regen) await generate(i, { customPrompt: promptDraft });
    } catch (e) {
      setImgError(apiError(e));
    }
  };

  const anyBusy = batchBusy || Object.values(busy).some(Boolean);

  return (
    <div className="mvp-timeline">
      <div className="mvp-panels-toolbar">
        <button
          className="btn-primary mvp-cta-sm"
          onClick={generateAll}
          disabled={anyBusy || disabled || panels.length === 0}
          data-testid="panels-generate-all-btn"
        >
          {batchBusy ? <Loader2 size={14} className="mvp-spin" /> : <Wand2 size={14} />}
          {batchBusy ? "Generating panels…" : "Generate All Panels"}
        </button>
        {batchBusy && (
          <span className="mvp-panels-batch-note">
            Panels generate sequentially, each using the previous as a style reference for consistency.
          </span>
        )}
      </div>
      {imgError && <div className="mvp-error" data-testid="panels-image-error">{imgError}</div>}
      {panels.map((p, i) => {
        const status = p.image_status;
        const generating = busy[i] || status === "generating";
        return (
          <div key={i} className="mvp-arc mvp-editable-row mvp-panel-card" data-testid={`panel-${i}`}>
            <RemoveItemBtn onClick={() => remove(i)} testid={`panel-remove-${i}`} inline />
            <span className="mvp-arc-num">Panel {p.panel || i + 1}</span>

            <div className="mvp-panel-media" data-testid={`panel-media-${i}`}>
              {p.image_url ? (
                <a href={p.image_url} target="_blank" rel="noreferrer">
                  <img src={p.image_url} alt={`Panel ${i + 1}`} data-testid={`panel-image-${i}`} />
                </a>
              ) : (
                <div className={`mvp-panel-empty ${generating ? "mvp-panel-generating" : ""}`}>
                  {generating ? (
                    <><Loader2 size={22} className="mvp-spin" /><span>Illustrating panel…</span></>
                  ) : (
                    <><ImageOff size={22} /><span>No image yet</span></>
                  )}
                </div>
              )}
              {status === "failed" && !generating && (
                <div className="mvp-panel-fail" data-testid={`panel-image-error-${i}`}>
                  <AlertTriangle size={14} /> {p.image_error || "Generation failed"}
                </div>
              )}
              {p.image_upscaled && (
                <span className="mvp-panel-upscaled-tag" title="This image was rendered at higher resolution">
                  <Sparkles size={11} /> Upscaled
                </span>
              )}
            </div>

            <div className="mvp-panel-buttons" data-testid={`panel-buttons-${i}`}>
              {!p.image_url ? (
                <button
                  className="btn-primary mvp-cta-sm"
                  onClick={() => generate(i)}
                  disabled={generating || disabled}
                  data-testid={`panel-generate-${i}`}
                >
                  <ImagePlus size={14} /> {generating ? "Generating…" : "Generate Image"}
                </button>
              ) : (
                <>
                  <button
                    className="mvp-nav-ghost"
                    onClick={() => generate(i)}
                    disabled={generating || disabled}
                    data-testid={`panel-regenerate-${i}`}
                  >
                    <RefreshCw size={13} /> Regenerate
                  </button>
                  <button
                    className="mvp-nav-ghost"
                    onClick={() => generate(i, { upscale: true })}
                    disabled={generating || disabled}
                    data-testid={`panel-upscale-${i}`}
                  >
                    <Sparkles size={13} /> Upscale
                  </button>
                </>
              )}
              <button
                className="mvp-nav-ghost"
                onClick={() => openPromptEditor(i)}
                disabled={generating || disabled}
                data-testid={`panel-edit-prompt-${i}`}
              >
                <Pencil size={13} /> Edit Prompt
              </button>
            </div>

            <EditableText as="p" multiline value={p.scene || ""} onChange={(v) => edit(i, { scene: v })} testid={`panel-scene-${i}`} emptyLabel="Describe the panel visually…" />
            <EditableText as="p" multiline className="mvp-dialogue" value={p.dialogue || ""} onChange={(v) => edit(i, { dialogue: v })} testid={`panel-dialogue-${i}`} emptyLabel="Dialogue or SFX…" />
          </div>
        );
      })}
      <button className="mvp-nav-ghost mvp-small-add" onClick={add} data-testid="panel-add" disabled={disabled}>
        <Plus size={13} /> Add panel
      </button>

      {promptEditIndex !== null && (
        <div className="mvp-modal-backdrop" onClick={() => setPromptEditIndex(null)}>
          <div className="mvp-modal mvp-modal-wide" onClick={(e) => e.stopPropagation()} data-testid="panel-prompt-modal">
            <button className="mvp-modal-close" onClick={() => setPromptEditIndex(null)} aria-label="Close">
              <X size={16} />
            </button>
            <h3>Edit image prompt — Panel {promptEditIndex + 1}</h3>
            <p>This prompt is sent to Nano Banana. Character appearance, world, and style hints are auto-appended when generating.</p>
            <textarea
              className="mvp-json-area mvp-prompt-area"
              value={promptDraft}
              onChange={(e) => setPromptDraft(e.target.value)}
              rows={8}
              data-testid="panel-prompt-textarea"
            />
            <div className="mvp-modal-actions">
              <button
                className="mvp-nav-ghost"
                onClick={() => setPromptEditIndex(null)}
                data-testid="panel-prompt-cancel"
              >
                Cancel
              </button>
              <button
                className="mvp-nav-ghost"
                onClick={() => savePromptEdit(false)}
                data-testid="panel-prompt-save"
              >
                <Check size={14} /> Save Prompt
              </button>
              <button
                className="btn-primary mvp-cta-sm"
                onClick={() => savePromptEdit(true)}
                data-testid="panel-prompt-regenerate"
              >
                <Wand2 size={14} /> Save & Regenerate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const RemoveItemBtn = ({ onClick, testid, inline = false }) => (
  <button
    className={`mvp-remove-btn ${inline ? "mvp-remove-btn-inline" : ""}`}
    onClick={onClick}
    aria-label="Remove"
    title="Remove"
    data-testid={testid}
  >
    <X size={13} />
  </button>
);
